/**
 * ═══════════════════════════════════════════════════════════════
 * DEVBRAINS — MEETING PLATFORM CONFIGURATION
 * ═══════════════════════════════════════════════════════════════
 *
 * Supported platforms:
 *   1. Google Meet   — link-based (no SDK needed)
 *   2. Zoom          — Zoom Meeting SDK (Web)
 *   3. Microsoft Teams — Teams Toolkit / deep-link
 *   4. Custom Link   — any URL
 *
 * ─── HOW TO CONFIGURE EACH PLATFORM ───────────────────────────
 *
 *  Google Meet:
 *    → No API key needed for basic link joining
 *    → For auto-creating meetings: needs Google Calendar API
 *    → TODO: See MEETING_INTEGRATIONS.md §1
 *
 *  Zoom:
 *    → Needs: Zoom Meeting SDK credentials
 *    → TODO: See MEETING_INTEGRATIONS.md §2
 *
 *  Microsoft Teams:
 *    → Needs: Azure App Registration (for auto-create)
 *    → TODO: See MEETING_INTEGRATIONS.md §3
 *
 * ═══════════════════════════════════════════════════════════════
 */

// ── Platform Definitions ────────────────────────────────────────
export const MEETING_PLATFORMS = [
  {
    id:          'google_meet',
    label:       'Google Meet',
    shortLabel:  'Meet',
    icon:        'google_meet',
    color:       '#1a73e8',
    bgColor:     '#e8f0fe',
    textColor:   '#1a73e8',
    placeholder: 'https://meet.google.com/abc-defg-hij',
    hint:        'Free for everyone · No app install needed',
    joinLabel:   'Join on Meet',
    sdkAvailable: false,
    autoCreate:  false,
    docsUrl:     'https://developers.google.com/calendar/api',
  },
  {
    id:          'zoom',
    label:       'Zoom',
    shortLabel:  'Zoom',
    icon:        'zoom',
    color:       '#2D8CFF',
    bgColor:     '#e6f2ff',
    textColor:   '#2D8CFF',
    placeholder: 'https://zoom.us/j/123456789?pwd=xxxxx',
    hint:        'Best for recordings and breakout rooms',
    joinLabel:   'Join on Zoom',
    sdkAvailable: true,
    autoCreate:  false,
    docsUrl:     'https://developers.zoom.us/docs/meeting-sdk/web/',
  },
  {
    id:          'teams',
    label:       'Microsoft Teams',
    shortLabel:  'Teams',
    icon:        'teams',
    color:       '#5059C9',
    bgColor:     '#edecfa',
    textColor:   '#5059C9',
    placeholder: 'https://teams.microsoft.com/l/meetup-join/...',
    hint:        'Best for corporate environments',
    joinLabel:   'Join on Teams',
    sdkAvailable: false,
    autoCreate:  false,
    docsUrl:     'https://learn.microsoft.com/en-us/microsoftteams/platform/',
  },
  {
    id:          'custom',
    label:       'Custom Link',
    shortLabel:  'Custom',
    icon:        'custom',
    color:       '#374151',
    bgColor:     '#f3f4f6',
    textColor:   '#374151',
    placeholder: 'https://your-meeting-link.com',
    hint:        'Skype, Jitsi, Whereby, or any other tool',
    joinLabel:   'Join Meeting',
    sdkAvailable: false,
    autoCreate:  false,
    docsUrl:     null,
  },
];

// ── Get platform by id ──────────────────────────────────────────
export const getPlatform = (id) =>
  MEETING_PLATFORMS.find(p => p.id === id) || MEETING_PLATFORMS[0];

// ── Zoom SDK Configuration ──────────────────────────────────────
// TODO §2.1: Replace placeholder values with real Zoom credentials
// Get from: https://marketplace.zoom.us/develop/create → Meeting SDK App
export const ZOOM_CONFIG = {
  // TODO: Add your Zoom Meeting SDK Key from Zoom Marketplace
  sdkKey: process.env.REACT_APP_ZOOM_SDK_KEY || 'YOUR_ZOOM_SDK_KEY',

  // TODO: Add your Zoom SDK Secret
  // ⚠️  NEVER put the real secret in frontend code
  // ⚠️  Generate signature from your BACKEND: POST /api/meetings/zoom/signature
  sdkSecret: 'GENERATE_FROM_BACKEND_NOT_HERE',

  // TODO: Set your Zoom Meeting SDK version
  // Check latest: https://www.npmjs.com/package/@zoom/meetingsdk
  sdkVersion: '3.x.x',

  // Zoom meeting embed options
  options: {
    debug: process.env.NODE_ENV === 'development',
    leaveUrl: `${window.location.origin}/dashboard`,
    showMeetingHeader: true,
    disableInvite: false,
    disableCallOut: false,
    disableRecord: false,
    disableJoinAudio: false,
    audioPanelAlwaysOpen: false,
    isSupportAV: true,
    isSupportChat: true,
    isSupportQA: false,
    isSupportCC: false,
    isSupportPolling: false,
    screenShare: true,
    rwcBackup: '',
    videoDrag: true,
    sharingMode: 'both',
    videoHeader: true,
    isLockBottom: true,
    isSupportNonverbal: true,
    isShowJoiningErrorDialog: true,
  },
};

// ── Google Meet Configuration ───────────────────────────────────
// TODO §1.1: For AUTO-CREATING Google Meet links (optional)
// Current: mentor pastes meet link manually — this works fine
// Enhanced: backend generates meet link via Google Calendar API
export const GOOGLE_MEET_CONFIG = {
  // TODO: Enable OAuth 2.0 in Google Cloud Console
  // https://console.cloud.google.com/ → APIs & Services → Credentials
  clientId: process.env.REACT_APP_GOOGLE_CLIENT_ID || '',

  // TODO: Enable these APIs in Google Cloud Console:
  // - Google Calendar API
  // - Google Meet API (if available in your region)
  scopes: [
    'https://www.googleapis.com/auth/calendar.events',
    'https://www.googleapis.com/auth/calendar',
  ],

  // Auto-create is disabled until credentials are set
  autoCreateEnabled: !!(process.env.REACT_APP_GOOGLE_CLIENT_ID),
};

// ── Microsoft Teams Configuration ──────────────────────────────
// TODO §3.1: For AUTO-CREATING Teams meetings (optional)
// Current: mentor pastes teams link manually — this works fine
// Enhanced: backend generates teams link via Microsoft Graph API
export const TEAMS_CONFIG = {
  // TODO: Register app in Azure Active Directory
  // https://portal.azure.com → Azure AD → App registrations → New
  clientId: process.env.REACT_APP_AZURE_CLIENT_ID || '',

  // TODO: Set your Azure AD tenant ID
  tenantId: process.env.REACT_APP_AZURE_TENANT_ID || 'common',

  // TODO: Add these permissions in Azure Portal → API Permissions:
  // - OnlineMeetings.ReadWrite
  // - Calendars.ReadWrite
  scopes: ['OnlineMeetings.ReadWrite'],

  // Auto-create is disabled until credentials are set
  autoCreateEnabled: !!(process.env.REACT_APP_AZURE_CLIENT_ID),
};

// ── Meeting URL Validators ──────────────────────────────────────
export const MEETING_URL_PATTERNS = {
  google_meet: /^https:\/\/meet\.google\.com\/[a-z]{3}-[a-z]{4}-[a-z]{3}$/,
  zoom:        /^https:\/\/[\w-]+\.zoom\.us\/j\/\d+/,
  teams:       /^https:\/\/teams\.microsoft\.com\/l\/meetup-join\//,
  custom:      /^https?:\/\/.+/,
};

export const validateMeetingUrl = (platform, url) => {
  if (!url) return { valid: false, message: 'Meeting URL is required' };
  const pattern = MEETING_URL_PATTERNS[platform];
  if (!pattern) return { valid: true, message: '' };
  if (!pattern.test(url)) {
    return {
      valid: false,
      message: `Invalid ${getPlatform(platform).label} URL format`,
    };
  }
  return { valid: true, message: '' };
};

// ── Generate meeting deep-link for joining ──────────────────────
export const getMeetingJoinUrl = (platform, url) => {
  if (!url) return null;

  switch (platform) {
    case 'zoom': {
      // Convert zoom web URL to zoom app deep link
      // https://zoom.us/j/123?pwd=xxx → zoommtg://zoom.us/join?confno=123&pwd=xxx
      try {
        const match = url.match(/zoom\.us\/j\/(\d+)(?:\?pwd=([^&]+))?/);
        if (match) {
          const confNo = match[1];
          const pwd    = match[2] || '';
          // Try to open in Zoom app; fallback to web
          return {
            appUrl: `zoommtg://zoom.us/join?confno=${confNo}&pwd=${pwd}`,
            webUrl: url,
          };
        }
      } catch (_) {}
      return { appUrl: null, webUrl: url };
    }

    case 'teams': {
      // Teams deep link: ms-teams.microsoft.com
      return {
        appUrl: url.replace('https://teams.microsoft.com', 'msteams:'),
        webUrl: url,
      };
    }

    case 'google_meet':
    case 'custom':
    default:
      return { appUrl: null, webUrl: url };
  }
};

// ── Reminder timing config ──────────────────────────────────────
export const MEETING_REMINDERS = {
  // Send email reminder X minutes before session
  emailReminderMinutes: [1440, 60, 15], // 24hr, 1hr, 15min
  // Show in-app alert X minutes before
  inAppAlertMinutes: 10,
};
